// External libraries
import { Component } from "react";

// FontAwesome icons
import { faArrowsRotate } from "@fortawesome/pro-duotone-svg-icons";

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { filterBy, orderBy } from "@progress/kendo-data-query";
import { ListView, ListViewHeader } from "@progress/kendo-react-listview";
import { Ripple } from "@progress/kendo-react-ripple";

// Internal components
import { ColorChangingIcon } from "../../Common/UI/ColorChangingIcon";
import { If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";
import { AdaptiveField } from "./FieldFactory";

// Utilities
import { Invisible, getEntriesByTemplateId, validateStageStep } from "../../Utilities/EntryUtils";

// Signals
import {
  allDatasets,
  getEntriesByTemplateId as getEntries,
  userEmail,
  userEntries,
} from "../../Common/Store/CommonSignals";
import {
  disabledSections,
  entryValues,
  galleryValues,
  isResyncingGallery,
  readOnlyEntry,
  resyncGalleryDifferences,
  resyncGalleryName,
  showGalleryResyncWindow,
  template,
} from "../../Entry/Manage/EditEntrySignals";

/**
 * Renders individual items in the gallery.
 * @param {Object} props - The properties passed to the ItemRender function.
 * @param {Object} props.e - The event object.
 * @param {Array} props.values - The values to render.
 * @param {Object} props.field - The field configuration.
 * @param {number} props.sectionIndex - The index of the section.
 * @param {number} props.fieldIndex - The index of the field.
 * @param {string} props.galleryName - The name of the gallery.
 * @param {boolean} props.isTabField - Indicates if it's a tab field.
 * @returns {JSX.Element} The rendered item.
 */
const ItemRender = ({
  e,
  values,
  field,
  sectionIndex,
  fieldIndex,
  galleryName,
  isTabField,
  isStageField,
  isPrintPreview,
  handleSubmit,
}) => {
  const fields = field.properties?.Fields;
  if (!values) return;

  const handleFieldChange = ({ e, eventIndex }) => {
    const newGalleryValues = JSON.tryParse(JSON.stringify(galleryValues.value));
    const fieldNameFromEvent = e.target.name.split("_")[0];

    // Find the existing key with case-insensitive comparison
    const existingKey = Object.keys(newGalleryValues[galleryName][eventIndex]).find(
      (k) => k.toLowerCase() === fieldNameFromEvent.toLowerCase()
    );

    // Use the existing key if found, otherwise use the fieldName from event
    const keyToUse = existingKey || fieldNameFromEvent;

    newGalleryValues[galleryName][eventIndex][keyToUse] = e?.value?.toString() ?? "";

    galleryValues.value = newGalleryValues;
    entryValues.value = {
      ...entryValues.value,
      [galleryName]: newGalleryValues[galleryName],
    };
  };

  function getApprovalMode(field, e) {
    if (!readOnlyEntry.value) return false;

    const approvalModeFields = [
      "Pre Approval Status",
      "Final Approval Status",
      "Comments",
      "Status",
      "Commentary",
      "Explanation",
      "Not Approved for Closure Commentary",
      "Send Notification",
    ];

    const approverNameFields = ["Department Approver", "Alternate Approver", "Approval Delegate"];

    let approverEmail = null;

    for (const dataField in values[e.index]) {
      if (approverNameFields.includes(dataField)) {
        const approverArray = JSON.tryParse(values[e.index][dataField], []);
        if (approverArray.length > 0 && approverArray[0]?.email) {
          approverEmail = approverArray[0].email;
          break;
        }
      }
    }

    return (
      approverEmail?.toLowerCase() === userEmail.value.toLowerCase() &&
      approvalModeFields.includes(field.fieldName)
    );
  }

  return (
    <span
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${fields?.length}, 1fr)`,
        columnGap: "10px",
        alignItems: "top",
        padding: "10px",
      }}
    >
      {fields.map((field) => {
        const visibilityResult = Invisible({
          VisibilityRules: field?.properties?.VisibilityRules,
          parameterValues: {},
          eventIndex: e.index,
        });

        if (visibilityResult.invisible) {
          values[e.index][field.fieldName] = "";
          delete entryValues.value[field.fieldName + "_" + e.index];
          return null;
        }

        let approvalMode = getApprovalMode(field, e);

        const props = {
          fieldName: field.fieldName + "_" + e.index,
          field: field,
          sectionIndex: sectionIndex,
          fieldIndex: fieldIndex,
          fieldType: field.fieldType,
          fontSize: field.properties?.fontSize,
          fontWeight: field.properties?.fontWeight,
          labelVisible: true,
          eventIndex: e.index,
          value: getValueIgnoreCase(values[e.index], field.fieldName),
          handleFieldChange: (data) => handleFieldChange({ ...data, galleryName }),
          galleryParentName: galleryName,
          isTabField: isTabField,
          isStageField: isStageField,
          isPrintPreview: isPrintPreview,
          approvalMode: readOnlyEntry.value && approvalMode,
          handleSubmit: handleSubmit,
        };

        return (
          <AdaptiveField
            key={field.fieldName + "_" + e.index}
            fieldType={field.fieldType}
            {...props}
          />
        );
      })}
    </span>
  );
};

/**
 * Retrieves a value from an object using a case-insensitive key lookup.
 * @param {Object} obj - The object to search within.
 * @param {string} fieldName - The field name to search for (case-insensitive).
 * @returns {*} The value associated with the field name, or undefined if not found.
 */
function getValueIgnoreCase(item, fieldName) {
  if (!item) return undefined;

  const key = Object.keys(item).find((k) => k.toLowerCase() === fieldName.toLowerCase());

  return key ? item[key] : undefined;
}

/**
 * Serializes an object to JSON string.
 * @param {Object} obj - The object to serialize.
 * @returns {string} The serialized JSON string.
 */
const serialize = (obj) => {
  return JSON.stringify(obj);
};

/**
 * Compares two lists and returns the differences.
 * @param {Array} list1 - The first list to compare.
 * @param {Array} list2 - The second list to compare.
 * @returns {Object} An object containing deletions and additions.
 */
const compareLists = (list1, list2) => {
  const list1Set = new Set(list1?.map(serialize));
  const list2Set = new Set(list2?.map(serialize));

  const deletions = [...list1Set]?.filter((item) => !list2Set?.has(item));
  const additions = [...list2Set]?.filter((item) => !list1Set?.has(item));

  return {
    deletions: deletions.map((item) => JSON.tryParse(item)),
    additions: additions.map((item) => JSON.tryParse(item)),
  };
};

/**
 * Renders the header for the gallery.
 * @param {Object} props - The properties for the header render.
 * @param {string} props.fieldName - The name of the field.
 * @param {Object} props.Data - The data configuration.
 * @param {Array} props.Values - The values in the gallery.
 * @param {Array} props.fields - The fields configuration.
 * @param {Array} props.exclusionFilterData - The data for exclusion filtering.
 * @param {string} props.ExclusionFilterValue - The value to use for exclusion filtering.
 * @returns {JSX.Element} The rendered header.
 */
const GalleryHeaderRender = ({
  label,
  Data,
  Values,
  fields,
  exclusionFilterData,
  ExclusionFilterValue,
  FieldInformation,
}) => {
  const galleryResync = () => {
    const dcData = getConnectionData({ Data });
    let newGalleryValues = dcData.map((value) => {
      let entryValue = {};

      if (fields) {
        fields.some((field) => {
          if (field.valueMap) {
            const index = Values.findIndex((val) => val[field.fieldName] === value[field.valueMap]);
            if (index !== -1) {
              entryValue = { ...entryValue, ...Values[index] };
              return true;
            } else {
              entryValue = { ...entryValue, ...{ [field.fieldName]: value[field.valueMap] } };
            }
          } else if (field.fieldType === "Checkbox") {
            entryValue[field.fieldName] = value[field.valueMap] || "false";
          } else if (field.fieldType === "Date") {
            const newValue = new Date();
            const timeZoneOffset = newValue.getTimezoneOffset() * 60000;
            const adjustedDate =
              new Date(newValue.getTime() - timeZoneOffset).toISOString().slice(0, 10) +
              "T00:00:00.000";
            entryValue[field.fieldName] =
              value[field.valueMap] || field.properties?.DefaultValue ? adjustedDate : null;
          } else if (field.fieldType === "DateTime") {
            const newValue = new Date();
            const timeZoneOffset = newValue.getTimezoneOffset() * 60000;
            const adjustedDate = new Date(newValue.getTime() - timeZoneOffset)
              .toISOString()
              .replace("Z", "");

            entryValue[field.fieldName] =
              value[field.valueMap] || field.properties?.DefaultValue ? adjustedDate : null;
          } else {
            entryValue[field.fieldName] =
              value[field.valueMap] || field?.properties?.DefaultValue || "";
          }
          return false;
        });
      }

      return entryValue;
    });

    if (ExclusionFilterValue) {
      newGalleryValues = newGalleryValues?.filter(
        (option) =>
          !exclusionFilterData?.some(
            (exclusionFilterOption) =>
              exclusionFilterOption?.[ExclusionFilterValue] === option?.[ExclusionFilterValue]
          )
      );
    }

    resyncGalleryDifferences.value = compareLists(Values, newGalleryValues);
  };

  return (
    <ListViewHeader className="!k-bg-white">
      <div className="k-d-flex k-bg-white k-vertical-align-center">
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <ColorChangingIcon
            title="Resync Gallery"
            className="k-mr-2"
            icon={faArrowsRotate}
            name={label + "_resync"}
            initialPrimaryColor="#0078d4"
            initialSecondaryColor="#a6e6ff"
            initialSize={24}
            hoverSize={24}
            onClick={() => {
              galleryResync();
              showGalleryResyncWindow.value = true;
              resyncGalleryName.value = label;
            }}
          />
          {label}
          <If
            condition={
              FieldInformation !== undefined &&
              FieldInformation !== "" &&
              FieldInformation !== "<p></p>"
            }
          >
            <TooltipPopupIcon content={FieldInformation} />
          </If>
        </div>
      </div>
    </ListViewHeader>
  );
};

/**
 * Retrieves connection data based on the provided configuration.
 * @param {Object} params - The parameters for getting connection data.
 * @param {Object} params.Data - The data configuration.
 * @returns {Array} The retrieved connection data.
 */
const getConnectionData = ({ Data, entries = undefined }) => {
  switch (Data?.type) {
    case "Template":
      return orderBy(filterBy(getEntriesByTemplateId(Data.id, entries), Data.filter), Data.sort);
    case "Global Parameters":
    case "List":
      return orderBy(
        filterBy(allDatasets.value?.find((dataset) => dataset.id === Data.id)?.values, Data.filter),
        Data.sort
      );
    default:
      return [];
  }
};

/**
 * GalleryField component for rendering and managing a gallery of fields.
 * @extends {Component}
 */
export class GalleryField extends Component {
  /**
   * Creates an instance of GalleryField.
   * @param {Object} props - The component props.
   * @param {Object} props.field - The field configuration.
   * @param {number} props.sectionIndex - The index of the section containing this field.
   * @param {number} props.fieldIndex - The index of this field within its section.
   * @param {boolean} props.isTabField - Indicates if it's a tab field.
   */
  constructor({
    field,
    sectionIndex,
    fieldIndex,
    isTabField,
    isStageField,
    isPrintPreview,
    handleSubmit,
  }) {
    super();
    const { fieldName, properties } = field;
    this.state = {
      Values: properties?.ParentFieldName
        ? this.optionsHelper(properties?.ParentFieldName, properties?.Dataset?.id)
        : properties?.Values || [],
      parentFieldValue: entryValues.value[properties?.ParentFieldName],
      exclusionFilterData: getConnectionData({ Data: properties?.ExclusionFilter }),
      invisible: false,
      disabled: false,
    };
    this.field = field;
    this.fieldName = fieldName;
    this.Data = properties?.Data;
    this.Values = properties?.Values;
    this.Height = properties?.Height || 300;
    this.ExclusionFilterValue = properties?.ExclusionFilterValue;
    this.visibilityRules = properties?.VisibilityRules;
    this.parameterValues = properties?.ParameterValues;
    this.parentFieldName = properties?.ParentFieldName;
    this.label = properties?.Label || `${fieldName}:`;
    this.fieldInformation = properties?.FieldInformation;
    this.ConnectionData = getConnectionData({ field, Data: this.Data });
    this.sectionIndex = sectionIndex;
    this.sectionName = template.value?.sections?.[sectionIndex]?.sectionName;
    this.fieldIndex = fieldIndex;
    this.isTabField = isTabField;
    this.isStageField = isStageField;
    this.isPrintPreview = isPrintPreview;
    this.handleSubmit = handleSubmit;
    this.disposeDependencyEffect = () => {};
    this.disposeResyncEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};
  }

  /**
   * Lifecycle method called after the component mounts.
   * Sets up initial gallery values and effects.
   */
  componentDidMount() {
    if (
      JSON.stringify(galleryValues.value[this.fieldName]) !==
      JSON.stringify(entryValues.value[this.fieldName])
    ) {
      galleryValues.value[this.fieldName] = entryValues.value[this.fieldName];
    }

    this.setState({ Values: entryValues.value[this.fieldName] });

    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });

    this.handleDependencyEffect();
    this.handleResyncEffect();

    this.initializeGalleryData();
  }

  async initializeGalleryData() {
    if (this.Data?.type === "Template") {
      if (!userEntries.value?.[this.Data.id]) {
        const entries = await getEntries(this.Data.id);
        this.setInitialGalleryValues({
          galleryName: this.fieldName,
          fields: this.field.properties.Fields,
          values: getConnectionData({ Data: this.Data, entries: entries }),
          callback: () => {
            this.setState({ Values: entryValues.value[this.fieldName] }, () => {
              const newGalleryValues = galleryValues.value;
              newGalleryValues[this.fieldName] = this.state.Values;
              galleryValues.value = newGalleryValues;
            });
          },
        });
      }
    } else if (!this.isTabField && !this.isStageField) {
      this.setInitialGalleryValues({
        galleryName: this.fieldName,
        fields: this.field.properties.Fields,
        values: this.ConnectionData,
        callback: () => {
          this.setState({ Values: entryValues.value[this.fieldName] }, () => {
            const newGalleryValues = galleryValues.value;
            newGalleryValues[this.fieldName] = this.state.Values;
            galleryValues.value = newGalleryValues;
          });
        },
      });
    }
  }

  /**
   * Lifecycle method called before the component unmounts.
   * Cleans up effects and handles field changes if necessary.
   */
  componentWillUnmount() {
    this.disposeDependencyEffect();
    this.disposeResyncEffect();
    this.disposeDisabledSectionsEffect();
  }

  /**
   * Lifecycle method called after the component updates.
   * Handles visibility changes and updates gallery values accordingly.
   */
  componentDidUpdate() {
    if (this.state.invisible && entryValues.value[this.fieldName] !== "") {
      this.setState(() => ({ Values: [] }));
      entryValues.value = { ...entryValues.value, [this.fieldName]: "" };
      galleryValues.value = { ...galleryValues.value, [this.fieldName]: "" };
    }
    if (!this.state.invisible && entryValues.value[this.fieldName] === "") {
      this.setInitialGalleryValues({
        galleryName: this.fieldName,
        fields: this.field.properties.Fields,
        values: this.ConnectionData,
        callback: () => {
          this.setState({ Values: entryValues.value[this.fieldName] }, () => {
            const newGalleryValues = galleryValues.value;
            newGalleryValues[this.fieldName] = this.state.Values;
            galleryValues.value = newGalleryValues;

            entryValues.value = {
              ...entryValues.value,
              [this.fieldName]: this.state.Values,
            };
          });
        },
      });
    }
  }

  /**
   * Determines if the component should update.
   * @param {Object} _ - Unused nextProps parameter.
   * @param {Object} nextState - The next state of the component.
   * @returns {boolean} Whether the component should update.
   */
  shouldComponentUpdate(_, nextState) {
    return (
      JSON.stringify(this.state.Values) !== JSON.stringify(nextState.Values) ||
      this.state.parentFieldValue !== nextState.parentFieldValue ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled
    );
  }

  /**
   * Sets up an effect to handle dependency changes.
   */
  handleDependencyEffect() {
    // Create a ref to track last filtered values to prevent loops
    this.lastFilteredValues = null;

    this.disposeDependencyEffect = effect(() => {
      // Get current parent field value
      const parentFieldValue = entryValues.value[this.parentFieldName];

      // Check visibility rules
      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
      });

      // Always get filtered options
      const filteredOptions = this.optionsHelper(this.parentFieldName, this.Data?.id);

      // Stringify for comparison to prevent infinite loops
      const filteredOptionsString = JSON.stringify(filteredOptions);

      // Only update if filtered results changed
      if (this.lastFilteredValues !== filteredOptionsString) {
        this.lastFilteredValues = filteredOptionsString;

        // Update state first
        this.setState({
          invisible: result.invisible && !result.disabled,
          disabled: result.invisible && result.disabled,
          Values: filteredOptions,
          parentFieldValue: parentFieldValue,
        });

        // Only update entry/gallery values if they're different lengths
        if (entryValues.value[this.fieldName].length !== JSON.parse(filteredOptionsString).length) {
          // Update gallery values
          const newGalleryValues = { ...galleryValues.value };
          newGalleryValues[this.fieldName] = filteredOptions;
          galleryValues.value = newGalleryValues;

          // Update entry values
          entryValues.value = {
            ...entryValues.value,
            [this.fieldName]: filteredOptions,
          };
        }
      } else {
        // Just update visibility state if needed
        this.setState({
          invisible: result.invisible && !result.disabled,
          disabled: result.invisible && result.disabled,
          parentFieldValue: parentFieldValue,
        });
      }
    });
  }

  /**
   * Sets up an effect to handle gallery resync.
   */
  handleResyncEffect() {
    this.disposeResyncEffect = effect(() => {
      if (isResyncingGallery.value && resyncGalleryName.value === this.fieldName) {
        const galleryName = resyncGalleryName.value;

        this.setState(
          (prevState) => {
            const newGalleryValues = JSON.tryParse(JSON.stringify(prevState.Values));
            resyncGalleryDifferences.value.additions.forEach((addition) => {
              newGalleryValues.push(addition);
            });
            galleryValues.value = {
              ...galleryValues.value,
              [galleryName]: newGalleryValues,
            };
            return { Values: newGalleryValues };
          },
          () => {
            entryValues.value = {
              ...entryValues.value,
              [galleryName]: this.state.Values,
            };
          }
        );
        showGalleryResyncWindow.value = false;
        resyncGalleryDifferences.value = {};
        resyncGalleryName.value = "";
        isResyncingGallery.value = false;
      }
    });
  }

  /**
   * Sets the initial values for the gallery.
   * @param {Object} params - The parameters for setting initial values.
   * @param {string} params.galleryName - The name of the gallery.
   * @param {Array} params.values - The initial values.
   * @param {Array} params.fields - The fields configuration.
   * @param {Function} params.callback - Callback function to execute after setting values.
   */
  setInitialGalleryValues({ galleryName, values, fields, callback }) {
    if (this.isPrintPreview) {
      return;
    }

    if (entryValues.value[this.fieldName] && entryValues.value[this.fieldName].length > 0) {
      return;
    }

    if (!fields) {
      entryValues.value = { ...entryValues.value, [galleryName]: [] };
      galleryValues.value = { ...galleryValues.value, [galleryName]: [] };
      callback();
      return;
    }

    let newGalleryValues = values.map((value) => {
      const entryValue = {};
      fields.forEach((field) => {
        if (field.fieldType === "Checkbox") {
          entryValue[field.fieldName] = value[field.valueMap] || "false";
          return;
        }
        if (field?.fieldType === "Time") {
          const newValue = new Date();
          const timeZoneOffset = newValue.getTimezoneOffset() * 60000;
          const adjustedDate = new Date(newValue.getTime() - timeZoneOffset)
            .toISOString()
            .replace("Z", "");

          entryValue[field.fieldName] = field.properties?.DefaultValue ? adjustedDate : null;
          return;
        }
        entryValue[field.fieldName] =
          value[field.valueMap] || field?.properties?.DefaultValue || "";
      });
      return entryValue;
    });

    if (this.ExclusionFilterValue) {
      newGalleryValues = newGalleryValues.filter(
        (option) =>
          !this.state.exclusionFilterData.some(
            (exclusionFilterOption) =>
              exclusionFilterOption?.[this.ExclusionFilterValue] ===
              option?.[this.ExclusionFilterValue]
          )
      );
    }

    if (this.parentFieldName) {
      newGalleryValues = this.optionsHelper(this.parentFieldName, this.Data.id);
    }

    entryValues.value = {
      ...entryValues.value,
      [galleryName]: newGalleryValues,
    };

    galleryValues.value = {
      ...galleryValues.value,
      [galleryName]: newGalleryValues,
    };

    if (this.isStageField) validateStageStep();

    callback();
  }

  optionsHelper(parentFieldName, datasetId) {
    if (!this.Values) return [];

    const parentFieldValue = entryValues.value[this.parentFieldName];

    if (!parentFieldName) return this.Values;

    const dataset = allDatasets.value.find((x) => x.id === datasetId);

    if (!dataset?.values) return [];

    // Get valid option values
    const options = dataset.values.filter((option) => option.filterValue === parentFieldValue);
    const firstField = Object.keys(this.Values[0] || {})[0];

    // Filter values based on options
    const filteredValues = this.Values.filter((item) =>
      options.some((option) => option.value === item[firstField])
    );

    // Deduplicate based ONLY on the Approvers field
    const uniqueApprovers = new Set();
    const uniqueValues = filteredValues.filter((item) => {
      const approverValue = item[firstField];
      if (uniqueApprovers.has(approverValue)) {
        return false; // Skip duplicates
      }
      uniqueApprovers.add(approverValue);
      return true;
    });

    return uniqueValues;
  }

  /**
   * Renders the GalleryField component.
   * @returns {JSX.Element|null} The rendered component or null if invisible.
   */
  render() {
    return (
      !this.state.invisible && (
        <div
          style={{ height: this.Height + "px" }}
          className="field-wrapper form-gallery k-overflow-auto"
        >
          <Ripple>
            <ListView
              data-testid={this.fieldName}
              className={this.state.disabled ? "gallery-disabled" : ""}
              data={this.state.Values || []}
              header={() =>
                GalleryHeaderRender({
                  label: this.label,
                  Data: this.Data,
                  Values: this.state.Values,
                  fields: this.field.properties.Fields,
                  exclusionFilterData: this.state.exclusionFilterData,
                  ExclusionFilterValue: this.ExclusionFilterValue,
                  ConnectionData: this.ConnectionData,
                  FieldInformation: this.fieldInformation,
                })
              }
              item={(e) =>
                ItemRender({
                  e,
                  values: entryValues.value[this.fieldName],
                  field: this.field,
                  sectionIndex: this.sectionIndex,
                  fieldIndex: this.fieldIndex,
                  galleryName: this.fieldName,
                  isTabField: this.isTabField,
                  isStageField: this.isStageField,
                  isPrintPreview: this.isPrintPreview,
                  handleSubmit: this.handleSubmit,
                })
              }
            />
          </Ripple>
        </div>
      )
    );
  }
}
